import { QRCodeCanvas } from "qrcode.react";
import { FaUser, FaCheckCircle } from "react-icons/fa";
import React, { forwardRef } from "react";

const CARD_WIDTH = 324;
const CARD_HEIGHT = 204;

const COLORS = {
  CARD_BG: "#EBBE4E",
  CARD_STRIP: "#C08F32",
  CARD_TEXT: "#6E3E1B",
  WHITE: "#ffffff",
};

const styles = {
  cardContainer: {
    display: "flex",
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    backgroundColor: COLORS.CARD_BG,
    padding: 15,
    borderRadius: 12,
    boxShadow: "0 8px 12px rgba(0,0,0,0.1)",
    position: "relative",
    overflow: "hidden",
  },
  cornerImage: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 40,
    height: 40,
    borderRadius: "50%",
    opacity: 0.8,
    zIndex: 10,
  },
  qrCodeContainer: {
    position: "absolute",
    bottom: 8,
    right: 8,
    width: 30,
    height: 30,
    borderRadius: 4,
    backgroundColor: COLORS.WHITE,
    padding: 2,
    zIndex: 5,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  absoluteCircle1: {
    position: "absolute",
    top: -20,
    right: -20,
    width: 80,
    height: 80,
    borderRadius: "50%",
    border: `2px solid ${COLORS.CARD_STRIP}50`,
  },
  absoluteCircle2: {
    position: "absolute",
    bottom: -30,
    left: -30,
    width: 100,
    height: 100,
    borderRadius: "50%",
    border: `1px solid ${COLORS.CARD_STRIP}30`,
  },
  leftColumn: {
    width: 85,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 10,
    marginRight: 15,
  },
  profilePicOuter: {
    width: 65,
    height: 65,
    borderRadius: "50%",
    backgroundColor: `${COLORS.CARD_STRIP}30`,
    border: `3px solid ${COLORS.CARD_STRIP}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  profilePic: {
    width: 50,
    height: 50,
    borderRadius: "50%",
  },
  logoContainer: {
    width: 55,
    height: 55,
    borderRadius: "50%",
    backgroundColor: COLORS.CARD_STRIP,
    border: `2px solid ${COLORS.CARD_STRIP}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  rightColumn: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 10,
    position: "relative",
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 3,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 18,
    fontWeight: "900",
    color: COLORS.CARD_TEXT,
    letterSpacing: 0.5,
  },
  dot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.CARD_TEXT,
    margin: "0 8px",
  },
  badgeText: {
    fontSize: 8,
    color: COLORS.WHITE,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: "10px",
  },
  subtitle: {
    fontSize: 9,
    color: COLORS.CARD_TEXT,
    fontStyle: "italic",
    opacity: 0.8,
    margin: 0,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.CARD_STRIP,
    margin: "6px 0",
    width: "80%",
    opacity: 0.6,
  },
  label: {
    fontSize: 7,
    color: COLORS.CARD_TEXT,
    fontWeight: 600,
    letterSpacing: 0.8,
    marginBottom: 0.5,
    opacity: 0.8,
  },
  value: {
    fontSize: 12,
    color: COLORS.CARD_TEXT,
    fontWeight: 700,
    margin: 0,
  },
  valueSmall: {
    fontSize: 9,
    color: COLORS.CARD_TEXT,
    fontWeight: 600,
  },
  cardNumber: {
    backgroundColor: COLORS.CARD_STRIP,
    padding: "2px 4px",
    borderRadius: 5,
    width: "fit-content",
    alignSelf: "flex-start",
    display: "inline-flex",
    opacity: 0.9,
  },
  cardNumberText: {
    fontSize: 10,
    color: COLORS.WHITE,
    fontWeight: 700,
    fontFamily: "monospace",
    margin: 0,
    lineHeight: "10px",
  },
  levelAndDOB: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingRight: 40,
  },
  levelBadge: {
    backgroundColor: COLORS.CARD_STRIP,
    padding: "2px 4px",
    borderRadius: 5,
    opacity: 0.8,
    display: "inline-flex",
    height: "14px",
  },
  levelText: {
    fontSize: 8,
    color: COLORS.WHITE,
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: "10px",
  },
  verifiedIcon: {
    position: "absolute",
    bottom: 5,
    right: 40,
    width: 20,
    height: 20,
    borderRadius: "50%",
    backgroundColor: COLORS.CARD_STRIP,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    opacity: 0.8,
  },
};

const backCardStyles = {
  backCard: {
    backgroundColor: COLORS.CARD_BG,
    padding: 20,
    borderRadius: 12,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
  },
  scrollView: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    textAlign: "center",
  },
  paragraph: {
    color: COLORS.CARD_TEXT,
    fontSize: 10,
    lineHeight: "14px",
    marginBottom: 8,
    maxWidth: "90%",
  },
  title: {
    color: COLORS.CARD_TEXT,
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 6,
  },
  text: {
    color: COLORS.CARD_TEXT,
    fontSize: 10,
    marginBottom: 4,
  },
};

const sheetStyles = {
  printRoot: {
    width: "100%",
    maxWidth: 794,
    margin: "0 auto",
    padding: 24,
    backgroundColor: COLORS.WHITE,
  },
  pages: {
    display: "flex",
    flexDirection: "column",
    gap: 24,
    alignItems: "center",
  },
  page: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    width: "100%",
    maxWidth: 794,
    minHeight: "calc(297mm - 48px)",
    breakAfter: "page",
    pageBreakAfter: "always",
  },
  pair: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: 8,
    backgroundColor: COLORS.WHITE,
    border: "1px solid rgba(192, 143, 50, 0.2)",
    borderRadius: 16,
    boxShadow: "0 8px 20px rgba(0,0,0,0.05)",
    breakInside: "avoid",
    pageBreakInside: "avoid",
  },
};

function chunkCards(cards, pageSize) {
  const chunks = [];

  for (let index = 0; index < cards.length; index += pageSize) {
    chunks.push(cards.slice(index, index + pageSize));
  }

  return chunks;
}

function formatDisplayDate(value) {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-GB");
}

function PatronCardFront({ receiptDetails }) {
  const memberName = receiptDetails?.full_name || "Loading...";
  const patronshipNo = receiptDetails?.patronship_no || "Loading...";
  const patronshipLevel =
    receiptDetails?.patronship_level ||
    receiptDetails?.scheme_patronship_level ||
    "Loading...";
  const dateOfBirth = formatDisplayDate(receiptDetails?.date_of_birth);

  return (
    <div style={styles.cardContainer}>
      <img
        src="https://www.kanpurfolk.in/WhatsApp%20Image%202025-05-06%20at%202.37.12%20PM%20(1).jpeg"
        alt="Corner"
        style={styles.cornerImage}
      />

      <div style={styles.qrCodeContainer}>
        <QRCodeCanvas
          value={receiptDetails?.patronship_no || "FOLK-PATRON"}
          size={25}
          fgColor={COLORS.CARD_TEXT}
          bgColor={COLORS.WHITE}
          level="L"
        />
      </div>

      <div style={styles.absoluteCircle1}></div>
      <div style={styles.absoluteCircle2}></div>

      <div style={styles.leftColumn}>
        <div style={styles.profilePicOuter}>
            <img
              src={receiptDetails.profile_pic || "https://images.rawpixel.com/image_png_800/cHJpdmF0ZS9sci9pbWFnZXMvd2Vic2l0ZS8yMDIzLTAxL3JtNjA5LXNvbGlkaWNvbi13LTAwMi1wLnBuZw.png"}
              alt="Profile"
              style={styles.profilePic}
            />
          {/* {receiptDetails?.profile_pic ? (
          ) : (
            <FaUser size={30} color={COLORS.CARD_TEXT} />
          )} */}
        </div>

        <div style={styles.logoContainer}>
          <img
            src="/WhatsApp Image 2025-05-06 at 2.34.06 PM (1).jpeg"
            alt="Logo"
            style={{ width: 50, height: 50 }}
          />
        </div>
      </div>

      <div style={styles.rightColumn}>
        <div>
          <div style={styles.titleRow}>
            <span style={styles.title}>FOLK</span>
            <div style={styles.dot} />
            <div style={styles.levelBadge}>
              <span style={styles.badgeText}>PATRON</span>
            </div>
          </div>
          <p style={styles.subtitle}>Family of Lord Krishna</p>
          <div style={styles.divider} />
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <p style={styles.label}>MEMBER NAME</p>
            <p style={styles.value}>{memberName}</p>
          </div>

          <div style={{ marginBottom: 2 }}>
            <p style={styles.label}>CARD NUMBER</p>
            <div style={styles.cardNumber}>
              <p style={styles.cardNumberText}>{patronshipNo}</p>
            </div>
          </div>

          <div style={styles.levelAndDOB}>
            <div className="mb-4" style={{ flex: 1 }}>
              <p style={styles.label}>LEVEL</p>
              <div style={styles.levelBadge}>
                <p style={styles.levelText}>{patronshipLevel}</p>
              </div>
            </div>
            <div className="mb-3" style={{ flex: 1 }}>
              <p style={styles.label}>DOB</p>
              <p style={styles.valueSmall}>{dateOfBirth || "-"}</p>
            </div>
          </div>
        </div>

        <div style={styles.verifiedIcon}>
          <FaCheckCircle size={12} color={COLORS.WHITE} />
        </div>
      </div>
    </div>
  );
}

function CardBack() {
  return (
    <div style={backCardStyles.backCard}>
      <div style={backCardStyles.scrollView}>
        <p style={backCardStyles.paragraph}>
          Whatever you do, whatever you eat, whatever you offer or give away,
          and whatever austerities you perform – do that, O son of Kunti, as an
          offering to Me.
        </p>

        <p style={backCardStyles.paragraph}>
          Thank you for your patronship in mission of spreading Krishna
          consciousness.
        </p>

        <p style={backCardStyles.title}>FOLK - Family of Lord Krishna (ISKCON)</p>
        <p style={backCardStyles.text}>+91 7080007066</p>
        <p style={backCardStyles.text}>www.folk.org.in</p>
      </div>
    </div>
  );
}

const PatronCardsSheet = forwardRef(({ cards = [] }, ref) => {
  const pages = chunkCards(cards, 4);

  return (
    <div ref={ref} style={sheetStyles.printRoot}>
      <div style={sheetStyles.pages}>
        {pages.map((pageCards, pageIndex) => (
          <div
            key={`page-${pageIndex}`}
            data-pdf-page="true"
            style={{
              ...sheetStyles.page,
              breakAfter: pageIndex === pages.length - 1 ? "auto" : "page",
              pageBreakAfter:
                pageIndex === pages.length - 1 ? "auto" : "always",
            }}
          >
            {pageCards.map((card, cardIndex) => (
              <div
                key={`${card?.patronship_no || card?.full_name || "patron"}-${pageIndex}-${cardIndex}`}
                style={sheetStyles.pair}
              >
                <PatronCardFront receiptDetails={card} />
                <CardBack />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

export default PatronCardsSheet;